import { Component, type ErrorInfo, type ReactNode } from 'react';
import apiClient from '../../api/client';
import AppStatePage from './AppStatePage';
import { isChunkLoadError, reloadOnceForChunkError } from '../../utils/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);

    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return;
    }

    apiClient.post('/errors/collect', {
      message: error.message,
      stack: error.stack,
      component_stack: info.componentStack,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    }).catch(() => { /* ignore logging failures */ });
  }

  handleRetry = () => {
    if (isChunkLoadError(this.state.error) && reloadOnceForChunkError()) {
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <AppStatePage
          statusColor="var(--rb-color-accent-negative, #DB0A40)"
          title={this.props.fallbackTitle || 'Something went wrong'}
          message={
            chunkError
              ? 'A newer version of Peak was deployed. Refresh the page to continue.'
              : this.state.error?.message || 'An unexpected error occurred. Please try again.'
          }
          actions={[
            {
              label: chunkError ? 'Refresh' : 'Try Again',
              onClick: this.handleRetry,
            },
            {
              label: 'Back to Dashboard',
              variant: 'secondary',
              onClick: () => window.location.assign('/'),
            },
          ]}
        />
      );
    }
    return this.props.children;
  }
}
